/**
 * Die Bereiche der Mitarbeiter-Desktop-Hülle — Reihenfolge, Beschriftung, Route, Gate.
 *
 * Bewusst ein pures Modul ohne DI: Welche Bereiche ein Mitarbeiter sieht, hängt an
 * vier unabhängigen Schaltern. Steht diese Entscheidung im Template, prüft sie
 * niemand; hier ist sie ohne TestBed prüfbar.
 *
 * Die Reihenfolge spiegelt die Tab-Leiste des Telefons (`Heute · Schichten ·
 * Zeiten · Aufgaben · Mehr`). Wer die App auf dem Handy kennt, soll auf dem
 * Rechner nicht umlernen müssen.
 */

/** Basis aller Bereichsrouten. Das Segment `dashboard` bleibt, weil sowohl der
 *  Redirect `/employeedashboard` als auch die Geräteweiche darauf zeigen. */
export const EMPLOYEE_SHELL_BASE = '/employee/dashboard';

export type EmployeeAreaId =
  | 'heute'
  | 'schichten'
  | 'zeiten'
  | 'aufgaben'
  | 'abwesenheit'
  | 'verfuegbarkeit'
  | 'dokumente'
  | 'profil';

export interface EmployeeNavArea {
  id: EmployeeAreaId;
  /** Übersetzungsschlüssel — niemals fertiger Text. */
  labelKey: string;
  icon: string;
  /** Absoluter Pfad, direkt für `routerLink` verwendbar. */
  route: string;
  /** `primary` = eigener Eintrag in der Leiste, `more` = im Menü „Mehr". */
  group: 'primary' | 'more';
  /** Trägt diese Fläche den Aufgabenzähler? */
  showsTasksBadge?: boolean;
}

/**
 * Die Schalter, von denen die Sichtbarkeit abhängt.
 *
 * Bewusst als flaches Objekt statt als Service-Zugriffe: so ist `buildEmployeeAreas`
 * eine reine Funktion, und die Hülle entscheidet an einer Stelle, woher die Werte
 * kommen.
 */
export interface EmployeeAreaGates {
  /** `CompanySettings.ShiftSettings.ShiftPlanerActive` */
  shiftPlannerActive: boolean;
  /** `CompanySettings.TimeTrackingSettings.TimeTrackingActive` */
  timeTrackingActive: boolean;
  /** 3-Zustands-Gate der Aufgaben, bereits auf `=== 'active'` reduziert. */
  tasksActive: boolean;
  /** `Feature.ABSENCE_MANAGEMENT_BASIC` */
  absenceActive: boolean;
}

/** Alle Bereiche in fester Reihenfolge, jeweils mit ihrer Sichtbarkeitsbedingung. */
const ALL_AREAS: ReadonlyArray<EmployeeNavArea & { isVisible: (g: EmployeeAreaGates) => boolean }> =
  [
    {
      id: 'heute',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_TODAY',
      icon: 'fa-light fa-house',
      route: `${EMPLOYEE_SHELL_BASE}/heute`,
      group: 'primary',
      isVisible: () => true,
    },
    {
      id: 'schichten',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_SHIFTS',
      icon: 'fa-light fa-calendar-days',
      route: `${EMPLOYEE_SHELL_BASE}/schichten`,
      group: 'primary',
      isVisible: (g) => g.shiftPlannerActive,
    },
    {
      id: 'zeiten',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_TIMES',
      icon: 'fa-light fa-clock',
      route: `${EMPLOYEE_SHELL_BASE}/zeiten`,
      group: 'primary',
      isVisible: (g) => g.timeTrackingActive,
    },
    {
      id: 'aufgaben',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_TASKS',
      icon: 'fa-light fa-list-check',
      route: `${EMPLOYEE_SHELL_BASE}/aufgaben`,
      group: 'primary',
      showsTasksBadge: true,
      isVisible: (g) => g.tasksActive,
    },
    {
      id: 'abwesenheit',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_ABSENCE',
      icon: 'fa-light fa-umbrella-beach',
      route: `${EMPLOYEE_SHELL_BASE}/abwesenheit`,
      group: 'more',
      isVisible: (g) => g.absenceActive,
    },
    {
      id: 'verfuegbarkeit',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_AVAILABILITY',
      icon: 'fa-light fa-calendar-week',
      route: `${EMPLOYEE_SHELL_BASE}/verfuegbarkeit`,
      group: 'more',
      isVisible: (g) => g.shiftPlannerActive,
    },
    {
      id: 'dokumente',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_DOCUMENTS',
      icon: 'fa-light fa-folder-open',
      route: `${EMPLOYEE_SHELL_BASE}/dokumente`,
      group: 'more',
      // Kein Gate: der Server maskiert fehlenden Zugriff mit 404, die Seite zeigt
      // dann einen Leerzustand. Ein Frontend-Gate würde die Maskierung unterlaufen.
      isVisible: () => true,
    },
    {
      id: 'profil',
      labelKey: 'EMPLOYEE_DESKTOP.AREA_PROFILE',
      icon: 'fa-light fa-user',
      route: `${EMPLOYEE_SHELL_BASE}/profil`,
      group: 'more',
      isVisible: () => true,
    },
  ];

/**
 * Die sichtbaren Bereiche für den angemeldeten Mitarbeiter.
 *
 * Ein abgeschalteter Bereich **verschwindet**, statt gesperrt zu erscheinen. Ein
 * Mitarbeiter kann kein Abo abschliessen — eine Kaufaufforderung wäre für ihn eine
 * Sackgasse, kein Angebot.
 */
export function buildEmployeeAreas(gates: EmployeeAreaGates): EmployeeNavArea[] {
  // Das Destructuring trennt die Sichtbarkeitsfunktion ab, damit sie nicht in der
  // Ausgabe landet — die Vorlage ist ein internes Detail. Die Unterstrich-Benennung
  // sagt dem Linter, dass der Wert absichtlich unbenutzt bleibt.
  return ALL_AREAS.filter((area) => area.isVisible(gates)).map(
    ({ isVisible: _isVisible, ...area }) => area
  );
}

/**
 * Darf dieser eine Bereich gezeigt werden?
 *
 * Dieselbe Bedingung wie in {@link buildEmployeeAreas}, nur für einen einzelnen
 * Bereich — der Routen-Wächter fragt damit **exakt dasselbe** wie die Leiste.
 * Vorher war das nicht so: die Leiste blendete einen abgeschalteten Bereich aus,
 * die Route liess ihn trotzdem zu. Ein Lesezeichen, ein Verlaufseintrag oder ein
 * Direktlink führte dadurch auf eine Fläche, die es für diesen Mandanten nicht
 * gibt — kein Sicherheitsloch (jeder sieht nur seine eigenen Daten), aber sie
 * sieht kaputt aus, statt gar nicht da zu sein.
 *
 * Ein **unbekannter** Bereich gilt als sichtbar. Wer eine neue Route einhängt und
 * das `data.area` vergisst, bekommt keine stille Sperre, sondern den Zustand von
 * vorher.
 */
export function isAreaVisible(id: EmployeeAreaId, gates: EmployeeAreaGates): boolean {
  const area = ALL_AREAS.find((candidate) => candidate.id === id);
  return area ? area.isVisible(gates) : true;
}

/**
 * Welcher Bereich gehört zu dieser URL?
 *
 * Vergleicht auf Segmentgrenze, damit ein späteres `/zeiten-alt` nicht als
 * `zeiten` durchgeht. Query-Parameter und Fragment werden abgeschnitten.
 * Ohne Treffer `null` — die Leiste hebt dann nichts hervor, statt zu raten.
 */
export function resolveActiveArea(url: string | null | undefined): EmployeeAreaId | null {
  if (!url) {
    return null;
  }

  const path = url.split('?')[0].split('#')[0];

  const match = ALL_AREAS.find(
    (area) => path === area.route || path.startsWith(`${area.route}/`)
  );

  return match ? match.id : null;
}
